import { useState, useEffect } from 'react'
import { profileApi, type CustomerProfile } from '../api/profileApi'
import { useAuth } from '../contexts/AuthContext'
import { formatNumberWithCommas, stripToNumber } from '../utils/formatCurrency'

const EMPLOYMENT_OPTIONS = [
  'Full-time',
  'Part-time',
  'Casual',
  'Self-employed',
  'Contract',
  'Unemployed',
  'Student',
  'Retired',
  'Other',
]

function splitFullName(fullName: string | undefined): { first: string; last: string } {
  if (!fullName?.trim()) return { first: '', last: '' }
  const parts = fullName.trim().split(/\s+/)
  const first = parts[0] ?? ''
  const last = parts.slice(1).join(' ') ?? ''
  return { first, last }
}

export default function CustomerProfileForm() {
  const { user, getAccessToken } = useAuth()
  const [profile, setProfile] = useState<CustomerProfile>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const load = async () => {
      const token = getAccessToken()
      if (!token) {
        setLoading(false)
        return
      }
      try {
        const { profile: p } = await profileApi.getCustomerProfile(token)
        const base = p || {}
        // Pre-populate with OAuth user data when profile fields are empty
        const { first, last } = splitFullName(user?.full_name)
        setProfile({
          ...base,
          first_name: base.first_name || first || undefined,
          last_name: base.last_name || last || undefined,
        })
      } catch {
        // API failed — still seed name from OAuth login data
        const { first, last } = splitFullName(user?.full_name)
        setProfile({ first_name: first || undefined, last_name: last || undefined })
        setError('Could not load saved profile')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [getAccessToken, user?.full_name])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const token = getAccessToken()
    if (!token) {
      setError('Please sign in to save your profile')
      return
    }
    setSaving(true)
    setError('')
    setSuccess(false)
    try {
      await profileApi.putCustomerProfile(token, profile)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch {
      setError('Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="card animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Your Profile</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {user?.email && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
            <input
              type="email"
              value={user.email}
              disabled
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-400">Email is managed by your sign-in provider</p>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First name</label>
            <input
              type="text"
              value={profile.first_name ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="John"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last name</label>
            <input
              type="text"
              value={profile.last_name ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Smith"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone number</label>
          <input
            type="tel"
            value={profile.phone_number ?? ''}
            onChange={(e) => setProfile((p) => ({ ...p, phone_number: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="0412 345 678"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date of birth</label>
          <input
            type="date"
            value={profile.date_of_birth ?? ''}
            onChange={(e) => setProfile((p) => ({ ...p, date_of_birth: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
          <textarea
            value={profile.address ?? ''}
            onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            rows={3}
            placeholder="Street, suburb, state, postcode"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Annual income ($)</label>
            <input
              type="text"
              inputMode="decimal"
              value={formatNumberWithCommas(String(profile.annual_income || ''))}
              onChange={(e) => {
                const raw = stripToNumber(e.target.value)
                setProfile((p) => ({
                  ...p,
                  annual_income: raw ? parseFloat(raw) : undefined,
                }))
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="85,000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employment status</label>
            <select
              value={profile.employment_status ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, employment_status: e.target.value || undefined }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select...</option>
              {EMPLOYMENT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Dependants</label>
          <input
            type="number"
            value={profile.dependants ?? ''}
            onChange={(e) =>
              setProfile((p) => ({
                ...p,
                dependants: e.target.value ? parseInt(e.target.value, 10) : undefined,
              }))
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="0"
            min={0}
            max={20}
          />
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            Profile saved successfully
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </form>
    </div>
  )
}
